import React from 'react';
import { useFormik } from 'formik';
import { Button, Col, Form, CustomInput, FormFeedback, FormGroup, Input, Row } from 'reactstrap';
import { validate } from 'utils/form-utils';
import * as Yup from 'yup';
import { FormikSelect } from 'components/inputs/FormikSelect';
import { getSelectOption, KNOW_MINDTRUST_SELECT_OPTIONS, TIME_ZONE_SELECT_OPTIONS } from 'types/select-types';
import { AdditionalInformationForm, additionalInformationFormDefaults } from 'types/talent-partners.types';

const validationSchema = () =>
  Yup.object()
    .shape({
      timezone: Yup.string().required('Time zone is required'),
      isAvailableTime: Yup.boolean().nullable().required('Field is required'),
      companyCertificates: Yup.string().nullable().notRequired(),
      learnMindtrust: Yup.string().nullable().required('Source is required'),
      whoReferred: Yup.string().when('learnMindtrust', {
        is: 'Referral',
        then: Yup.string().required('Referral name is required'),
      }),
      referralCode: Yup.string().nullable().notRequired(),
      details: Yup.string().nullable().notRequired(),
    })
    .required();

interface Props {
  step5: AdditionalInformationForm | null;
  onNext: (talentPartners: AdditionalInformationForm) => void;
  goBack: () => void;
}

export const TalentPartners5: React.FC<Props> = ({ step5, onNext, goBack }) => {
  const formik = useFormik<AdditionalInformationForm>({
    enableReinitialize: true,
    initialValues:
      {
        ...step5,
      } || additionalInformationFormDefaults(),
    validateOnMount: true,
    onSubmit: onNext,
    validate: validate<AdditionalInformationForm>(validationSchema),
  });
  const { values, errors, touched, isValid } = formik;

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="flow-content">
        <h1>Additional Information</h1>
        <p>How did you first learn of MindTrust?</p>
        <FormGroup>
          <label>
            Time zone <span className="error-message">*</span>
          </label>
          <FormikSelect
            name="timezone"
            placeholder="Select timezone"
            invalid={touched.timezone && !!errors[`timezone`]}
            valid={touched.timezone && !errors[`timezone`]}
            value={getSelectOption(values.timezone, TIME_ZONE_SELECT_OPTIONS) || TIME_ZONE_SELECT_OPTIONS[0]}
            options={TIME_ZONE_SELECT_OPTIONS}
            setFieldValue={(fieldName, fieldValue, validate) => {
              formik.setFieldValue(fieldName, fieldValue, validate);
              if (!values.workingTimezonePreference) {
                formik.setFieldValue('workingTimezonePreference', fieldValue, validate);
                formik.setFieldTouched('workingTimezonePreference', true, validate);
              }
            }}
            setFieldTouched={formik.setFieldTouched}
          />
          <FormFeedback>{errors.timezone}</FormFeedback>
        </FormGroup>
        <FormGroup className="d-flex w-100 justify-content-between align-items-center">
          <label>
            Can you work at least 4 hours in the UTC-05:00 (Eastern US) time zone between 8:00 a.m. and 5:00 p.m.?{' '}
            <span className="error-message">*</span>
          </label>
          <FormGroup className="d-flex justify-content-end align-items-center mb-0 pt-0">
            <CustomInput
              type="radio"
              id="part-time-yes"
              label="Yes"
              onChange={() => {
                formik.setFieldValue('isAvailableTime', false, true);
              }}
              required
              checked={!values.isAvailableTime}
            />
            <CustomInput
              className="ml-3"
              type="radio"
              id="part-time-no"
              label="No"
              onChange={() => {
                formik.setFieldValue('isAvailableTime', true, true);
              }}
              required
              checked={!!values.isAvailableTime}
            />
          </FormGroup>
        </FormGroup>
        <FormGroup>
          <label htmlFor="companyCertificates">What certificates does your company currently hold?</label>
          <Input
            name="companyCertificates"
            type="textarea"
            placeholder="AWS Certifications, Google Cloud Certifications, HubSpot Certifications, Salesforce Certifications, Google Ads Certifications, etc."
            valid={touched.companyCertificates && !errors.companyCertificates}
            invalid={touched.companyCertificates && !!errors.companyCertificates}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={values.companyCertificates || ''}
          />
          <FormFeedback>{errors.companyCertificates}</FormFeedback>
        </FormGroup>
        <FormGroup>
          <label>How did you first learn of MindTrust?</label>
          <FormikSelect
            invalid={touched.learnMindtrust && !!errors[`learnMindtrust`]}
            valid={touched.learnMindtrust && !errors[`learnMindtrust`]}
            name="learnMindtrust"
            value={
              getSelectOption(values.learnMindtrust, KNOW_MINDTRUST_SELECT_OPTIONS) || KNOW_MINDTRUST_SELECT_OPTIONS[0]
            }
            options={KNOW_MINDTRUST_SELECT_OPTIONS}
            setFieldTouched={formik.setFieldTouched}
            setFieldValue={formik.setFieldValue}
          />
          <i className="glyphicon glyphicon-user form-control-feedback" />
          <FormFeedback>{errors.learnMindtrust}</FormFeedback>
        </FormGroup>
        {(getSelectOption(values.learnMindtrust, KNOW_MINDTRUST_SELECT_OPTIONS)?.value === 'Referral' ||
          KNOW_MINDTRUST_SELECT_OPTIONS[0]?.value === 'Referral') && (
          <FormGroup>
            <label htmlFor="whoReferred">Who referred you?</label>
            <Input
              type="text"
              name="whoReferred"
              placeholder="Who referred you?"
              valid={touched.whoReferred && !errors.whoReferred}
              invalid={touched.whoReferred && !!errors.whoReferred}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={values.whoReferred || undefined}
            />
            <FormFeedback>{errors.whoReferred}</FormFeedback>
          </FormGroup>
        )}
        <FormGroup>
          <label htmlFor="referralCode">Referral code</label>
          <Input
            type="text"
            name="referralCode"
            placeholder="Referral code"
            valid={touched.referralCode && !errors.referralCode}
            invalid={touched.referralCode && !!errors.referralCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={values.referralCode || undefined}
          />
          <FormFeedback>{errors.referralCode}</FormFeedback>
        </FormGroup>
        <FormGroup>
          <label htmlFor="details">Anything else you would like to share?</label>
          <Input
            type="text"
            name="details"
            placeholder="Anything else you would like to share?"
            valid={touched.details && !errors.details}
            invalid={touched.details && !!errors.details}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={values.details || undefined}
          />
          <FormFeedback>{errors.details}</FormFeedback>
        </FormGroup>
      </div>
      <Row className="mt-4">
        <Col xs={6}>
          <Button className="btn-block c-gray" color="default" onClick={goBack}>
            <i className="btn-icon-back" />
            Back
          </Button>
        </Col>
        <Col xs={6}>
          <Button className="btn-block" type="submit" color="primary" disabled={!isValid}>
            Next
            <i className="btn-icon-next" />
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
